import React, { useEffect, useState } from "react";
import { api } from "../../services/api";
import toast from "react-hot-toast";
import {
  Settings as SettingsIcon,
  Key,
  Save,
  Eye,
  EyeOff,
  Shield,
  AlertCircle,
  CheckCircle,
} from "lucide-react";

const Settings: React.FC = () => {
  const [apiKey, setApiKey] = useState("");
  const [geminiModel, setGeminiModel] = useState("gemini-pro");
  const [settingsId, setSettingsId] = useState("");
  const [showApiKey, setShowApiKey] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [hasApiKey, setHasApiKey] = useState(false);
  const [claudeApiKey, setClaudeApiKey] = useState("");
  const [claudeModel, setClaudeModel] = useState("");
  const [showClaudeApiKey, setShowClaudeApiKey] = useState(false);
  const [isSavingClaude, setIsSavingClaude] = useState(false);

  useEffect(() => {
    const fetchApiKey = async () => {
      setIsLoading(true);
      try {
        const res = await api.get("/backend/settings");
        if (res && res.data && res.data.settings[0]) {
          const settingsRecord = res.data.settings[0];
          setSettingsId(settingsRecord._id);

          if (settingsRecord.gemini_api_key2) {
            const key = settingsRecord.gemini_api_key2;
            setApiKey(key);
            setGeminiModel(settingsRecord.gemini_model2 || "");
            setHasApiKey(key.length > 0);
          }

          if (settingsRecord.gemini_api_key) {
            setClaudeApiKey(settingsRecord.gemini_api_key);
            setClaudeModel(settingsRecord.gemini_model || "");
          }
        }
      } catch (err: any) {
        toast.error(err.message || "Failed to fetch API key");
      } finally {
        setIsLoading(false);
      }
    };

    fetchApiKey();
  }, []);

  const handleSave = async () => {
    if (!apiKey.trim()) {
      toast.error("Please enter a valid API key");
      return;
    }

    setIsSaving(true);
    try {
      const res = await api.put(`/backend/settings/${settingsId}/update`, {
        gemini_api_key2: apiKey.toString(),
        gemini_model2: geminiModel,
      });

      if (res) {
        setHasApiKey(true);
        toast.success("Settings updated successfully");
      }
    } catch (err: any) {
      toast.error(err.message || "Failed to update settings");
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveClaude = async () => {
    if (!claudeApiKey.trim()) {
      toast.error("Please enter a valid API key");
      return;
    }

    setIsSavingClaude(true);
    try {
      const res = await api.put(`/backend/settings/${settingsId}/update`, {
        gemini_api_key: claudeApiKey.toString(),
        gemini_model: claudeModel,
      });

      if (res) {
        toast.success("Settings updated successfully");
      }
    } catch (err: any) {
      toast.error(err.message || "Failed to update settings");
    } finally {
      setIsSavingClaude(false);
    }
  };

  const toggleShowApiKey = () => {
    setShowApiKey(!showApiKey);
  };

  const toggleShowClaudeApiKey = () => {
    setShowClaudeApiKey(!showClaudeApiKey);
  };

  // Mask API key for display
  const getMaskedApiKey = (key: string) => {
    if (!key) return "";
    if (key.length <= 12) return "•".repeat(key.length);
    return (
      key.slice(0, 6) +
      "•".repeat(Math.min(key.length - 10, 20)) +
      key.slice(-4)
    );
  };

  return (
    <div className="bg-white">
      {/* Main Content */}
      <div className="p-6">
        {/* Status Card */}
        {/* <div
          className={`mb-6 rounded-xl p-4 border ${
            hasApiKey
              ? "bg-green-50 border-green-200"
              : "bg-yellow-50 border-yellow-200"
          }`}
        >
          <div className="flex items-start gap-3">
            {hasApiKey ? (
              <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" />
            ) : (
              <AlertCircle className="w-5 h-5 text-yellow-600 mt-0.5" />
            )}
            <div>
              <h3
                className={`text-sm font-semibold ${
                  hasApiKey ? "text-green-900" : "text-yellow-900"
                }`}
              >
                {hasApiKey ? "API Key Configured" : "API Key Required"}
              </h3>
              <p
                className={`text-xs mt-1 ${
                  hasApiKey ? "text-green-700" : "text-yellow-700"
                }`}
              >
                {hasApiKey
                  ? "Your Gemini API key is set up and ready to use."
                  : "Please add your Gemini API key to enable AI features."}
              </p>
            </div>
          </div>
        </div> */}

        {/* Settings Form */}
        <div className="bg-gray-50 rounded-xl p-6">
          <div className="flex items-center gap-2 mb-6">
            <Key className="w-5 h-5 text-indigo-600" />
            <h3 className="text-lg font-semibold text-gray-900">
              Gemini API Configuration
            </h3>
          </div>

          {isLoading ? (
            <div className="space-y-3">
              <div className="h-4 w-32 rounded bg-gray-200 animate-pulse" />
              <div className="h-12 rounded-xl bg-gray-200 animate-pulse" />
              <div className="h-10 rounded-xl bg-gray-200 animate-pulse mt-4" />
            </div>
          ) : (
            <>
              <div className="space-y-4">
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Gemini API Key
                  </label>
                  <div className="relative">
                    <input
                      type={showApiKey ? "text" : "password"}
                      value={apiKey}
                      onChange={(e) => setApiKey(e.target.value)}
                      placeholder="Enter your Gemini API key..."
                      className="
                        w-full
                        rounded-xl
                        border border-gray-200
                        px-4 py-3
                        pr-12
                        text-sm
                        outline-none
                        focus:border-indigo-500
                        focus:ring-2
                        focus:ring-indigo-200
                        transition-all
                        bg-white
                      "
                    />
                    <button
                      onClick={toggleShowApiKey}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition"
                      type="button"
                    >
                      {showApiKey ? (
                        <EyeOff className="w-4 h-4" />
                      ) : (
                        <Eye className="w-4 h-4" />
                      )}
                    </button>
                  </div>
                  {apiKey && !showApiKey && (
                    <p className="text-xs text-gray-500 mt-1">
                      Current key: {getMaskedApiKey(apiKey)}
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Gemini Model
                  </label>
                  <input
                    type="text"
                    value={geminiModel.toLowerCase()}
                    onChange={(e) => setGeminiModel(e.target.value)}
                    placeholder="e.g., Gemini-3-haiku-20240307..."
                    className="
                      w-full
                      rounded-xl
                      border border-gray-200
                      px-4 py-3
                      text-sm
                      outline-none
                      focus:border-indigo-500
                      focus:ring-2
                      focus:ring-indigo-200
                      transition-all
                      bg-white
                    "
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Enter the Gemini model name for API requests
                  </p>
                </div>
              </div>

              <button
                onClick={handleSave}
                disabled={isSaving}
                className="
                  mt-6
                  w-full
                  rounded-xl
                  bg-gradient-to-r
                  from-indigo-600
                  to-purple-600
                  text-white
                  text-sm
                  font-medium
                  py-3
                  hover:shadow-lg
                  active:scale-[0.98]
                  transition-all
                  disabled:opacity-50
                  disabled:cursor-not-allowed
                  flex
                  items-center
                  justify-center
                  gap-2
                "
              >
                {isSaving ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4" />
                    Save API Key
                  </>
                )}
              </button>
            </>
          )}
        </div>

        {/* Claude API Configuration */}
        <div className="bg-gray-50 rounded-xl p-6 mt-6">
          <div className="flex items-center gap-2 mb-6">
            <Key className="w-5 h-5 text-indigo-600" />
            <h3 className="text-lg font-semibold text-gray-900">
              Claude API Configuration
            </h3>
          </div>

          {isLoading ? (
            <div className="space-y-3">
              <div className="h-4 w-32 rounded bg-gray-200 animate-pulse" />
              <div className="h-12 rounded-xl bg-gray-200 animate-pulse" />
              <div className="h-10 rounded-xl bg-gray-200 animate-pulse mt-4" />
            </div>
          ) : (
            <>
              <div className="space-y-4">
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Claude API Key
                  </label>
                  <div className="relative">
                    <input
                      type={showClaudeApiKey ? "text" : "password"}
                      value={claudeApiKey}
                      onChange={(e) => setClaudeApiKey(e.target.value)}
                      placeholder="Enter your Claude API key..."
                      className="
                        w-full
                        rounded-xl
                        border border-gray-200
                        px-4 py-3
                        pr-12
                        text-sm
                        outline-none
                        focus:border-indigo-500
                        focus:ring-2
                        focus:ring-indigo-200
                        transition-all
                        bg-white
                      "
                    />
                    <button
                      onClick={toggleShowClaudeApiKey}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition"
                      type="button"
                    >
                      {showClaudeApiKey ? (
                        <EyeOff className="w-4 h-4" />
                      ) : (
                        <Eye className="w-4 h-4" />
                      )}
                    </button>
                  </div>
                  {claudeApiKey && !showClaudeApiKey && (
                    <p className="text-xs text-gray-500 mt-1">
                      Current key: {getMaskedApiKey(claudeApiKey)}
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Claude Model
                  </label>
                  <input
                    type="text"
                    value={claudeModel.toLowerCase()}
                    onChange={(e) => setClaudeModel(e.target.value)}
                    placeholder="e.g., claude-sonnet-5..."
                    className="
                      w-full
                      rounded-xl
                      border border-gray-200
                      px-4 py-3
                      text-sm
                      outline-none
                      focus:border-indigo-500
                      focus:ring-2
                      focus:ring-indigo-200
                      transition-all
                      bg-white
                    "
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Enter the Claude model name for API requests
                  </p>
                </div>
              </div>

              <button
                onClick={handleSaveClaude}
                disabled={isSavingClaude}
                className="
                  mt-6
                  w-full
                  rounded-xl
                  bg-gradient-to-r
                  from-indigo-600
                  to-purple-600
                  text-white
                  text-sm
                  font-medium
                  py-3
                  hover:shadow-lg
                  active:scale-[0.98]
                  transition-all
                  disabled:opacity-50
                  disabled:cursor-not-allowed
                  flex
                  items-center
                  justify-center
                  gap-2
                "
              >
                {isSavingClaude ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4" />
                    Save API Key
                  </>
                )}
              </button>
            </>
          )}
        </div>

        {/* Tester Domain Card */}
      </div>
    </div>
  );
};

export default Settings;
